import { Fragment, useMemo, useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import Proptypes from "prop-types";
import { requestDataToSnakeCase } from "@shared/v2/caseTransformingAxios";
import Toast from "@shared/Toast";
import { CheckSolidV6, TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import { getAddressAttributes } from "@shared/v2/HandwrittenCard";
import { isUSCountry } from "@shared/v2/utils";
import AddMarketReportModal from "../../AddMarketReportModal";
import { deleteMarketReportApi } from "../../shared/serviceHelpers";
import { viewPhoneFromFlat } from "../../shared/models/Communications/viewPhone";
import { setCallParams as setCallParameters } from "../../reducers/dialerReducer/dialerReducer";
import PersonDetailsModal from "../../Contacts/PersonDetailsModal";
import { dynamicRow } from "../../Transactions/utils/dynamicRow";
import cellRenderers from "../columns/cellRenderers";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";
import { getMarketerOnboardingStatus } from "../../PersonDetail/actions/personDetailActionCreators";
import { AddorRemoveClass } from "./SelectOption";

const personPrimaryPhone = (person) => {
  const phones = person.dialable_phone_numbers.map(viewPhoneFromFlat);
  return phones.find((phone) => phone.isPrimary) || phones[0];
};

const mapStateToProps = (state) => ({
  messagingServiceUrl: state.leadIndex.messagingServiceUrl,
  people: state.leadIndex.people,
  currentUser: state.leadIndex.currentUser,
  account: state.leadIndex.account,
  platformAccounts: state.leadIndex.platformAccounts,
  idxDataUri: state.leadIndex.idxDataUri,
  idxDataToken: state.leadIndex.idxDataToken,
  source: state.leadIndex.from,
  featureFlags: state.layout.featureFlags,
});

const LeadRowsDynamic = ({
  setCallParams,
  messagingServiceUrl,
  people = [],
  currentUser = null,
  account = null,
  platformAccounts = false,
  idxDataUri = null,
  idxDataToken = null,
  source = null,
  featureFlags,
  renderableColumns = null,
}) => {
  const currentUserData = useCurrentUser();
  const isAdmin = currentUserData?.account?.isAdmin;
  const isAgent = currentUserData?.account?.isAgent;
  const isUSAccount = isUSCountry(currentUserData?.account?.country);
  const dispatch = useDispatch();
  const marketerOnboardingStatus = useSelector((state) => state.personDetail.marketerOnboardingStatus);

  useEffect(() => {
    dispatch(getMarketerOnboardingStatus());
  }, []);

  const { team_onboarded: teamOnboarded, agent_onboarded: agentOnboarded } =
    marketerOnboardingStatus?.data || {};

  const isOnboarded = (isAdmin && teamOnboarded) || (isAgent && teamOnboarded && agentOnboarded) || false;

  const [leadMatch, setLeadMatch] = useState(null);
  const [editPersonId, setEditPersonId] = useState(null);

  const [collectionNotificationToast, setCollectionNotificationToast] = useState({
    show: false,
    type: "",
    msg: "",
  });

  const onToggleCollectionNotification = ({ show, type, msg }) =>
    setCollectionNotificationToast({ show, type, msg });

  const hasIdxInfo = (person) => {
    if (
      (person.idx_property_views && person.idx_property_views !== 0) ||
      (person.idx_favorites && person.idx_favorites !== 0)
    ) {
      return true;
    }
    return false;
  };

  const lastVisit = (person) => {
    let lastvisit = "\u2014";
    if (person.person.idx_last_visit) {
      lastvisit = person.idx_last_visit_time;
    } else if (person.source && person.source === "Brivity IDX") {
      lastvisit = person.last_idx_visit_created_at;
    } else {
      lastvisit = "\u2014";
    }
    return lastvisit;
  };
  const Visits = (person) => {
    let idxvisit = "\u2014";
    if (person.person.idx_visits && person.person.idx_visits !== 0) {
      idxvisit = person.person.idx_visits;
    } else if (person.source && person.source === "Brivity IDX") {
      idxvisit = "1";
    } else {
      idxvisit = "\u2014";
    }
    return idxvisit;
  };

  const getCheckedCheckboxesFor = () => document.querySelectorAll("input.multi-select:checked").length;

  const handleSelectRows = () => {
    const numberOfCheckedRows = getCheckedCheckboxesFor();
    if (numberOfCheckedRows === people.length) {
      document.getElementById("select-rows").checked = true;
      document.getElementById("select-rows").indeterminate = false;
    } else if (numberOfCheckedRows === 0) {
      document.getElementById("select-rows").checked = false;
      document.getElementById("select-rows").indeterminate = false;
    } else {
      document.getElementById("select-rows").checked = false;
      document.getElementById("select-rows").indeterminate = true;
    }

    document.getElementById("is-all-selected").value = false;
    AddorRemoveClass("select-page");

    const leadSelectedEvent = new Event("lead-row:selected");
    window.dispatchEvent(leadSelectedEvent);
  };

  return (
    <>
      {collectionNotificationToast.show && (
        <Toast
          text={collectionNotificationToast.msg}
          containerClassName={`${collectionNotificationToast.show ? "tw-mt-[-210px] tw-opacity-100" : "tw-mt-[-80px] tw-opacity-0"} tw-p-12px tw-z-[9991] tw-transform tw--translate-x-1/2 tw-transition-all tw-duration-500
            ${collectionNotificationToast.type === "error" ? "tw-bg-[#FAE6E6] tw-border-[#FAC5C3]" : "tw-bg-[#EDFAF1] tw-border-[#CAF0D6]"}`}
          textClassName={
            collectionNotificationToast.type === "error" ? "tw-text-[#E5342E]" : "tw-text-[#177335]"
          }
          iconClassName={
            collectionNotificationToast.type === "error" ? "tw-text-[#E5342E]" : "tw-text-[#177335]"
          }
          Icon={collectionNotificationToast.type === "error" ? TriangleExclamationSolidV6 : CheckSolidV6}
        />
      )}
      <tbody>
        {people.map((_person) => {
          const person = useMemo(
            () => ({
              ...requestDataToSnakeCase(_person),
              leadPond: _person.leadPond,
              primaryAgent: _person.primaryAgent,
            }),
            [_person],
          );
          const [showAddModal, setShowModal] = useState(false);
          const [editReportId, setEditId] = useState(false);
          const [leadPond, setLeadPond] = useState(person?.leadPond);
          const [primaryAgent, setPrimaryAgent] = useState(person?.primaryAgent);
          const deleteMarketReport = async (id, primaryAgentUuid) =>
            new Promise((resolve) => {
              deleteMarketReportApi({
                data: { id, primary_agent_uuid: primaryAgentUuid },
                successCb: resolve,
                errorCb: () => resolve(false),
              });
            });

          const setMarketModal = (open) => {
            setShowModal(open);
            setEditId(null);
          };
          const editMarketReport = (id) => {
            setShowModal(id !== null);
            setEditId(id);
          };
          const primaryPhone = personPrimaryPhone(person);

          const isSendMailDisabled = !(
            getAddressAttributes({
              ...person.person,
              included: person.addresses?.data,
            }).length > 0 && isOnboarded
          );

          const keyedCells = cellRenderers({
            person,
            currentUser,
            account,
            platformAccounts,
            source,
            featureFlags,
            isUSAccount,
            isSendMailDisabled,
            isOnboarded,
            messagingServiceUrl,
            idxDataUri,
            idxDataToken,
            setCallParams,
            primaryPhone,
            handleSelectRows,
            hasIdxInfo,
            lastVisit,
            Visits,
            leadPond,
            primaryAgent,
            setLeadPond,
            setPrimaryAgent,
            setLeadMatch,
            setEditPersonId,
            setMarketModal,
            editMarketReport,
            deleteMarketReport,
            onToggleCollectionNotification,
          });

          const columns = renderableColumns ? dynamicRow(renderableColumns, keyedCells) : null;

          return (
            <Fragment key={person.id}>
              <tr
                className="person partial"
                data-login_user_id={person.person.login_user_id}
                data-type_name={person.person.type_name}
                data-slug={person.person.slug}
                data-full-name={source !== "leads" ? person.name_or_no_name : ""}
                data-fetch_results_count={source === "leads" ? person.fetch_results_count : ""}
                data-searched={source === "leads" && person.lead_last_search_at !== null ? true : ""}
                data-lead-id={person.person_lead_id}
                data-object-id={person.person_id}
                data-person-id={person.person_id}
                data-person-uuid={person.person.uuid}
                data-primary-agent={person.primary_agent_id}
                data-primary-email={source !== "leads" ? person.primary_email_address : ""}
                key={person.person.id}
              >
                {columns}
              </tr>
              {showAddModal && (
                <AddMarketReportModal
                  person={{
                    ...person,
                    emails: person.email_details.map((e) => ({ category: e.category, address: e.value })),
                    addresses: [person?.display_a_street_address],
                  }}
                  primaryAgent={person?.primaryAgent?.uuid}
                  isOpen
                  close={() => setMarketModal(false)}
                  id={editReportId}
                />
              )}
            </Fragment>
          );
        })}
        <PersonDetailsModal
          show={Boolean(leadMatch) || Boolean(editPersonId)}
          personId={leadMatch?.id || editPersonId}
          prefilledValues={leadMatch?.prefilledValues}
          onClose={() => {
            setLeadMatch(null);
            setEditPersonId(null);
          }}
        />
      </tbody>
    </>
  );
};

LeadRowsDynamic.propTypes = {
  setCallParams: Proptypes.func.isRequired,
  messagingServiceUrl: Proptypes.string.isRequired,
  people: Proptypes.arrayOf(
    Proptypes.shape({
      fullName: Proptypes.string,
    }),
  ),
  currentUser: Proptypes.shape({
    id: Proptypes.number,
    managedPhoneNumber: Proptypes.string,
    routingNumber: Proptypes.shape({ value: Proptypes.string }),
    fullName: Proptypes.string.isRequired,
  }),
  account: Proptypes.shape({
    uuid: Proptypes.string,
    address: Proptypes.shape({ geocodeString: Proptypes.string }),
    teamName: Proptypes.string.isRequired,
    fullName: Proptypes.string,
  }),
  platformAccounts: Proptypes.bool,
  idxDataUri: Proptypes.string,
  idxDataToken: Proptypes.string,
  source: Proptypes.string,
  featureFlags: Proptypes.shape().isRequired,
  renderableColumns: Proptypes.shape({}),
};

export default connect(mapStateToProps, { setCallParams: setCallParameters })(LeadRowsDynamic);
